import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { partnerSupplier } from '@/utils/environment';
import { transformAlertCards } from '@/experiences/sales-portal/domain/insights/blocs/transformers/alert-transformer';
import { useMemo } from 'react';
import { useSalesPortalGetActiveAlertsQuery } from '@/experiences/sales-portal/domain/insights/blocs/documents/active-alerts.generated';

/** Result of {@link useActiveAlertsLoader}. */
export interface UseActiveAlertsLoaderResult {
  alerts: Alert[];
  error: Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Loads the partner organization's ACTIVE hub alerts for the Insights ("For
 * You") feed and maps the raw `card_data_json` payload to domain {@link Alert}s.
 *
 * @returns {@link UseActiveAlertsLoaderResult}
 *
 * @example
 * const { alerts, loading } = useActiveAlertsLoader();
 */
export const useActiveAlertsLoader = (): UseActiveAlertsLoaderResult => {
  const { data, loading, error, refetch } = useSalesPortalGetActiveAlertsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    },
  });

  const alerts = useMemo(
    () => transformAlertCards(data?.getAllHubAlerts.card_data_json ?? []),
    [data],
  );

  return {
    alerts,
    error: error ?? null,
    loading: loading && !data,
    refetch: () => {
      void refetch();
    },
  };
};
